import React from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useNotify } from 'react-admin';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import {
    isImageAttachment,
    isPdfAttachment,
    QuoteAttachment,
} from '@/utils/quote_attachment_utils';

export type GalleryFile = {
    id: number | string;
    url: string;
    thumb_url?: string | null;
    content_type: string;
    original_filename?: string | null;
};

interface FileGalleryProps {
    files?: GalleryFile[] | null;
    /**
     * Turns a file url/thumb_url into a URL that <img> and the preview dialog can load
     * (e.g. an authenticated blob URL). Must be a stable reference (module-level fn).
     */
    resolveUrl: (url: string) => Promise<string>;
}

const fileName = (file: GalleryFile) =>
    file.original_filename || `attachment-${file.id}`;

interface GalleryThumbProps {
    file: GalleryFile;
    resolveUrl: (url: string) => Promise<string>;
}

// Thumbnails only for images: non-image thumb_url points to the full file, so never fetch it.
const GalleryThumb: React.FC<GalleryThumbProps> = ({ file, resolveUrl }) => {
    const isImage = isImageAttachment(file.content_type);
    const isPdf = isPdfAttachment({
        content_type: file.content_type,
        filename: fileName(file),
    });
    const [src, setSrc] = React.useState<string | null>(null);
    const [failed, setFailed] = React.useState(false);
    const [loading, setLoading] = React.useState(isImage && !!file.thumb_url);

    React.useEffect(() => {
        if (!isImage || !file.thumb_url) return;
        let cancelled = false;
        resolveUrl(file.thumb_url)
            .then((url) => !cancelled && setSrc(url))
            .catch(() => !cancelled && setFailed(true))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [file.thumb_url, isImage, resolveUrl]);

    if (isPdf) {
        return (
            <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} aria-hidden />
        );
    }
    if (isImage && loading) {
        return <CircularProgress size={20} />;
    }
    if (isImage && src && !failed) {
        return (
            <Box
                component="img"
                src={src}
                alt=""
                loading="lazy"
                onError={() => setFailed(true)}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                }}
            />
        );
    }
    if (isImage) {
        return <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} aria-hidden />;
    }
    return (
        <InsertDriveFileIcon sx={{ fontSize: 40, color: 'text.secondary' }} aria-hidden />
    );
};

/** Read-only grid of files: image thumbnails, or a file-type icon plus the name. Click opens a preview. */
export const FileGallery: React.FC<FileGalleryProps> = ({ files, resolveUrl }) => {
    const [preview, setPreview] = React.useState<QuoteAttachment | null>(null);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const notify = useNotify();

    // Early return must stay after all hooks (Rules of Hooks).
    if (!files?.length) return null;

    const handleClick = async (file: GalleryFile) => {
        const name = fileName(file);
        setPreview({
            id: `gallery-file-${file.id}`,
            name,
            filename: name,
            content_type: file.content_type || 'application/octet-stream',
        });
        setPreviewUrl(null);
        setPreviewLoading(true);
        try {
            setPreviewUrl(await resolveUrl(file.url));
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Could not load file',
                { type: 'error' },
            );
            setPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    mt: 0.75,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                    gap: 1,
                }}
            >
                {files.map((file) => {
                    const name = fileName(file);
                    return (
                        <ButtonBase
                            key={file.id}
                            onClick={() => handleClick(file)}
                            aria-label={`Preview ${name}`}
                            sx={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                textAlign: 'left',
                            }}
                        >
                            <Box
                                sx={{
                                    height: 80,
                                    bgcolor: 'grey.100',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                }}
                            >
                                <GalleryThumb file={file} resolveUrl={resolveUrl} />
                            </Box>
                            <Typography
                                variant="caption"
                                title={name}
                                sx={{
                                    px: 0.75,
                                    py: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {name}
                            </Typography>
                        </ButtonBase>
                    );
                })}
            </Box>

            <QuoteAttachmentPreviewDialog
                attachment={preview}
                sourceUrl={previewUrl}
                loading={previewLoading}
                onClose={() => {
                    setPreview(null);
                    setPreviewUrl(null);
                }}
            />
        </>
    );
};

FileGallery.displayName = 'FileGallery';
